// src/BatchManager.jsx
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "./apiBase"; // Adjust path if needed

// LocalStorage keys
const LS_BATCHES = "inventory:batches:v1";
const LS_ITEMS = "inventory:items:v1";

// Inline CSS injected once into <head>
const BATCH_MANAGER_CSS = `
.batch-manager-root { font-size: 12px; }

.batch-manager-header{
  display:flex; align-items:center; justify-content:space-between;
  gap:8px; flex-wrap:wrap; margin-bottom:6px;
}
.batch-manager-header-main{ display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
.batch-manager-stats{
  font-size:11px; opacity:0.9; display:flex; align-items:center; gap:6px; flex-wrap:wrap;
}

/* History panel */
.batch-history-panel{
  margin-top:6px; margin-bottom:10px; padding:8px;
  border-radius:10px; border:1px solid rgba(148,163,184,0.4);
  background:rgba(15,23,42,0.85);
}
.batch-history-header{
  display:flex; align-items:center; justify-content:space-between; gap:6px; margin-bottom:8px;
}
.batch-history-title{ font-size:11px; font-weight:600; opacity:0.9; }
.batch-filters-row{ display:flex; flex-wrap:wrap; gap:6px; margin-bottom:8px; }
.batch-filters-row input{
  font-size:11px; padding:4px 6px; border-radius:6px;
  border:1px solid rgba(148,163,184,0.6);
  background:rgba(15,23,42,0.9); color:#e5e7eb; min-width:120px;
}
.batch-history-list{
  max-height:220px; overflow-y:auto; display:flex; flex-direction:column; gap:4px;
}
.batch-history-item{
  border-bottom:1px solid rgba(148,163,184,0.35);
  padding-bottom:4px; padding-top:2px;
}
.batch-history-item-main{
  display:flex; justify-content:space-between; gap:8px; align-items:flex-start;
}
.batch-history-item-text{ min-width:0; }
.batch-history-meta{ font-size:10px; opacity:0.9; }
.batch-history-actions{ display:flex; flex-direction:column; gap:4px; }

/* Modal overlay + content */
.batch-manager-root .modal-overlay{
  position:fixed; inset:0; background:rgba(15,23,42,0.75);
  display:flex; align-items:center; justify-content:center; z-index:50;
}
.batch-manager-root .modal-content{
  background:#020617; border-radius:14px; border:1px solid rgba(148,163,184,0.5);
  padding:12px; width:min(920px, 100% - 24px);
  max-height:92vh; overflow-y:auto; box-shadow:0 18px 45px rgba(0,0,0,0.65);
}

/* Generic buttons */
.btn{
  border-radius:999px; border:1px solid transparent;
  padding:4px 10px; font-size:11px; cursor:pointer;
  background:#1f2937; color:#e5e7eb;
  display:inline-flex; align-items:center; justify-content:center; gap:4px;
  transition:background 0.15s ease, border-color 0.15s ease, transform 0.08s ease;
}
.btn.small{ padding:3px 9px; font-size:10px; }
.btn.primary{
  background:linear-gradient(135deg, #4f46e5, #22c55e);
  border-color:rgba(34,197,94,0.8); color:white;
}
.btn.ghost{
  background:rgba(15,23,42,0.85);
  border-color:rgba(148,163,184,0.6);
}
.btn:hover{ transform:translateY(-0.5px); background:#111827; }
.btn.primary:hover{ background:linear-gradient(135deg, #6366f1, #22c55e); }

/* Form layout */
.customize-row{ display:flex; align-items:flex-start; gap:6px; margin-bottom:6px; }
.customize-label{ min-width:110px; font-size:11px; opacity:0.9; }
.customize-controls{ flex:1; display:flex; flex-direction:column; gap:2px; }
.customize-controls input[type="text"],
.customize-controls input[type="number"],
.customize-controls input[type="date"],
.customize-controls select{
  font-size:11px; padding:4px 6px; border-radius:6px;
  border:1px solid rgba(148,163,184,0.6);
  background:rgba(15,23,42,0.9); color:#e5e7eb;
}

.field-error{ margin-top:2px; font-size:10px; color:#fecaca; }

/* Preview */
.label-preview-card{
  border-radius:8px; border:1px solid rgba(148,163,184,0.5);
  padding:8px; background:rgba(15,23,42,0.6);
}
.label-preview-inner{
  border:1px dashed rgba(148,163,184,0.8);
  padding:8px; border-radius:6px; font-size:11px;
  background:rgba(15,23,42,0.9);
}

/* Item defaults card */
.item-defaults-card{
  border-radius:8px; border:1px solid rgba(148,163,184,0.5);
  padding:6px 8px; background:rgba(15,23,42,0.9);
  margin-top:4px; font-size:11px;
}
.item-defaults-header{
  display:flex; justify-content:space-between; align-items:center;
  gap:6px; margin-bottom:4px;
}
.item-defaults-fields{ display:flex; flex-direction:column; gap:4px; }
.item-defaults-fields input{
  font-size:11px; padding:3px 6px; border-radius:6px;
  border:1px solid rgba(148,163,184,0.6);
  background:rgba(15,23,42,0.9); color:#e5e7eb;
}
.item-defaults-actions{ display:flex; gap:6px; flex-wrap:wrap; margin-top:4px; }

/* Print settings mini card */
.print-settings-card{
  border-radius:8px; border:1px solid rgba(148,163,184,0.5);
  padding:8px; background:rgba(15,23,42,0.55); font-size:11px;
}

.batch-history-list::-webkit-scrollbar,
.batch-manager-root .modal-content::-webkit-scrollbar{ width:6px; }
.batch-history-list::-webkit-scrollbar-thumb,
.batch-manager-root .modal-content::-webkit-scrollbar-thumb{
  background:rgba(148,163,184,0.6); border-radius:999px;
}
`;

// CSV escaping
const escapeCsv = (value) => {
  if (value === null || value === undefined) return "";
  const str = String(value);
  if (/[",\n\r]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
  return str;
};

export default function BatchManager({ embedded = false, onClose }) {
  const navigate = useNavigate();

  // Inject CSS once
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (!document.getElementById("batch-manager-css")) {
      const style = document.createElement("style");
      style.id = "batch-manager-css";
      style.textContent = BATCH_MANAGER_CSS;
      document.head.appendChild(style);
    }
  }, []);

  // Open modal immediately when mounted
  const [showModal, setShowModal] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const [autoSync, setAutoSync] = useState(false);

  // ✅ DEFAULT: XPrinter XP-365B roll labels 58mm × 40mm
  // Bigger text by default but still fits in the same frame:
  const [printSettings, setPrintSettings] = useState({
    mode: "ROLL_58x40",
    rollWmm: 58,
    rollHmm: 40,
    rollMarginMm: 1.5,     // if clipping: set 2.0
    rollFontScale: 1.12,   // ✅ bigger by default (0.75..1.25)
    rotate: false,         // if sideways, enable
    dense: true,           // tighter layout for 58×40
  });

  const [batches, setBatches] = useState(() => {
    if (typeof window === "undefined") return [];
    try {
      const raw = localStorage.getItem(LS_BATCHES);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });

  const [items, setItems] = useState(() => {
    if (typeof window === "undefined") return [];
    try {
      const raw = localStorage.getItem(LS_ITEMS);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });

  const [filters, setFilters] = useState({ query: "", from: "", to: "" });

  const [form, setForm] = useState({
    itemName: "",
    companyName: "",
    source: "",
    recipeNumber: "",
    productionDate: "",
    expiryDate: "",
    count: "",
    shelfLifeDays: "",
  });

  const [errors, setErrors] = useState({});
  const [syncingId, setSyncingId] = useState(null);
  const [syncError, setSyncError] = useState(null);

  // Item defaults editor
  const [showDefaultsEditor, setShowDefaultsEditor] = useState(false);
  const [defaultsForm, setDefaultsForm] = useState({
    companyName: "",
    source: "",
    recipeNumber: "",
  });

  // Persist batches
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      localStorage.setItem(LS_BATCHES, JSON.stringify(batches));
    } catch {}
  }, [batches]);

  // Persist items
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      localStorage.setItem(LS_ITEMS, JSON.stringify(items));
    } catch {}
  }, [items]);

  const resetForm = () => {
    setErrors({});
    setSyncError(null);
    setForm((prev) => ({
      itemName: "",
      companyName: "",
      source: "",
      recipeNumber: "",
      productionDate: "",
      expiryDate: "",
      count: "",
      shelfLifeDays: prev.shelfLifeDays || "",
    }));
    setShowDefaultsEditor(false);
  };

  const closeModal = () => {
    setShowModal(false);
    if (onClose) onClose();
    else if (!embedded) navigate(-1);
  };

  const findItemByName = (name) => {
    const key = (name || "").trim().toLowerCase();
    if (!key) return null;
    return items.find((it) => it.name.trim().toLowerCase() === key) || null;
  };

  const handleChangeGeneric = (e) => {
    const { name, value, checked, type } = e.target;

    if (name === "autoSync") {
      setAutoSync(!!checked);
      return;
    }

    if (name.startsWith("print_")) {
      const k = name.replace("print_", "");
      setPrintSettings((prev) => ({
        ...prev,
        [k]:
          type === "checkbox" ? !!checked :
          type === "number" ? Number(value) :
          value,
      }));
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleItemNameChange = (e) => {
    const value = e.target.value;
    const trimmed = value.trim();
    const matched = findItemByName(trimmed);

    setForm((prev) => {
      if (matched) {
        return {
          ...prev,
          itemName: value,
          companyName: matched.companyName || "",
          source: matched.source || "",
          recipeNumber: matched.recipeNumber || "",
        };
      }
      return { ...prev, itemName: value };
    });

    setErrors((prev) => ({ ...prev, itemName: undefined }));

    if (showDefaultsEditor) {
      if (matched) {
        setDefaultsForm({
          companyName: matched.companyName || "",
          source: matched.source || "",
          recipeNumber: matched.recipeNumber || "",
        });
      } else {
        setDefaultsForm({ companyName: "", source: "", recipeNumber: "" });
      }
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    const trimmedItemName = form.itemName.trim();
    const trimmedCompanyName = form.companyName.trim();
    const trimmedSource = form.source.trim();
    const trimmedRecipe = form.recipeNumber.trim();
    const trimmedCount = String(form.count ?? "").trim();

    const nextErrors = {};
    if (!trimmedItemName) nextErrors.itemName = "Item name is required.";
    if (!trimmedCompanyName) nextErrors.companyName = "Company name is required.";
    if (!form.productionDate) nextErrors.productionDate = "Production date is required.";
    if (!form.expiryDate) nextErrors.expiryDate = "Expiry date is required.";

    if (!trimmedCount) nextErrors.count = "Count is required.";
    else if (isNaN(Number(trimmedCount))) nextErrors.count = "Count must be a number.";
    else if (Number(trimmedCount) <= 0) nextErrors.count = "Count must be greater than zero.";

    return {
      isValid: Object.keys(nextErrors).length === 0,
      errors: nextErrors,
      trimmed: {
        itemName: trimmedItemName,
        companyName: trimmedCompanyName,
        source: trimmedSource,
        recipeNumber: trimmedRecipe,
        countStr: trimmedCount,
      },
    };
  };

  // Sync batch to backend
  const syncBatchToBackend = async (batch) => {
    if (!batch) return;
    setSyncError(null);
    setSyncingId(batch.id);

    try {
      const matchedItem = findItemByName(batch.itemName);

      const payload = {
        batch_id: batch.id,
        item_name: batch.itemName,
        company_name: batch.companyName,
        source: batch.source,
        recipe_number: batch.recipeNumber,
        production_date: batch.productionDate,
        expiry_date: batch.expiryDate,
        count: batch.count,
        created_at: batch.createdAt,
        item_master_id: matchedItem ? matchedItem.id : null,
      };

      const res = await fetch(`${API}/inventory/batches`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Backend responded with ${res.status}: ${text.slice(0, 180)}`);
      }

      alert("Batch synced to Inventory / CK backend.");
    } catch (err) {
      console.error("Sync error:", err);
      setSyncError("Could not sync to backend. Check API /inventory/batches or network.");
    } finally {
      setSyncingId(null);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const { isValid, errors: nextErrors, trimmed } = validate();
    if (!isValid) {
      setErrors(nextErrors);
      return;
    }

    const countNum = Number(trimmed.countStr);
    const existingItem = findItemByName(trimmed.itemName);

    const newBatch = {
      id: Date.now(),
      itemName: trimmed.itemName,
      companyName: trimmed.companyName,
      source: trimmed.source || null,
      recipeNumber: trimmed.recipeNumber || null,
      productionDate: form.productionDate,
      expiryDate: form.expiryDate,
      count: countNum,
      createdAt: new Date().toISOString(),
      itemMasterId: existingItem ? existingItem.id : null,
    };

    setBatches((prev) => [newBatch, ...prev]);

    if (!existingItem) {
      const newItem = {
        id: Date.now() + 1,
        name: trimmed.itemName,
        companyName: trimmed.companyName,
        source: trimmed.source,
        recipeNumber: trimmed.recipeNumber,
      };
      setItems((prev) => [...prev, newItem]);
    }

    alert("Batch created and saved.");

    if (autoSync) syncBatchToBackend(newBatch);
  };

  // Auto-fill expiry date
  const handleAutoExpiry = () => {
    if (!form.productionDate || !form.shelfLifeDays) {
      setErrors((prev) => ({
        ...prev,
        expiryDate: "Set production date and shelf life (days) to auto-calculate expiry.",
      }));
      return;
    }
    const days = Number(form.shelfLifeDays);
    if (isNaN(days) || days <= 0) {
      setErrors((prev) => ({ ...prev, shelfLifeDays: "Shelf life must be a positive number." }));
      return;
    }
    try {
      const base = new Date(form.productionDate + "T00:00:00");
      base.setDate(base.getDate() + days);
      const iso = base.toISOString().slice(0, 10);
      setForm((prev) => ({ ...prev, expiryDate: iso }));
      setErrors((prev) => ({ ...prev, expiryDate: undefined }));
    } catch {
      setErrors((prev) => ({ ...prev, expiryDate: "Could not calculate expiry date." }));
    }
  };

  const useAsTemplate = (batch) => {
    setErrors({});
    setSyncError(null);
    setForm((prev) => ({
      ...prev,
      itemName: batch.itemName || "",
      companyName: batch.companyName || "",
      source: batch.source || "",
      recipeNumber: batch.recipeNumber || "",
      productionDate: batch.productionDate || "",
      expiryDate: batch.expiryDate || "",
      count: batch.count != null ? String(batch.count) : "",
    }));
  };

  const deleteBatch = (id) => {
    if (typeof window !== "undefined") {
      if (!window.confirm("Delete this batch from history?")) return;
    }
    setBatches((prev) => prev.filter((b) => b.id !== id));
  };

  // CSV export
  const exportCsv = (mode = "all") => {
    const rows = mode === "filtered" ? filteredBatches : batches;
    if (!rows || !rows.length) {
      alert("No batches to export.");
      return;
    }
    const headers = [
      "id",
      "itemName",
      "companyName",
      "source",
      "recipeNumber",
      "productionDate",
      "expiryDate",
      "count",
      "createdAt",
      "itemMasterId",
    ];
    const lines = [
      headers.join(","),
      ...rows.map((b) => headers.map((key) => escapeCsv(b[key])).join(",")),
    ];
    const csv = lines.join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const suffix = mode === "filtered" ? "filtered" : "all";
    a.download = `batches_${suffix}.csv`;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const totalLabels = batches.reduce((sum, b) => sum + (b.count || 0), 0);
  const lastBatch = batches[0];

  const filteredBatches = useMemo(() => {
    const q = filters.query.trim().toLowerCase();
    const from = filters.from ? new Date(filters.from + "T00:00:00") : null;
    const to = filters.to ? new Date(filters.to + "T23:59:59") : null;

    return (batches || []).filter((b) => {
      if (q) {
        const text = [
          b.itemName,
          b.companyName,
          b.source,
          b.recipeNumber,
          b.productionDate,
          b.expiryDate,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!text.includes(q)) return false;
      }

      if (from || to) {
        if (!b.productionDate) return false;
        const prod = new Date(b.productionDate + "T00:00:00");
        if (from && prod < from) return false;
        if (to && prod > to) return false;
      }

      return true;
    });
  }, [batches, filters]);

  const hasPreviewData =
    form.itemName ||
    form.companyName ||
    form.productionDate ||
    form.expiryDate ||
    form.source ||
    form.recipeNumber;

  // Item defaults editor
  const toggleDefaultsEditor = () => {
    const currentName = form.itemName.trim();
    if (!currentName) {
      alert("Please enter an item name first.");
      return;
    }
    const existing = findItemByName(currentName);
    if (existing) {
      setDefaultsForm({
        companyName: existing.companyName || "",
        source: existing.source || "",
        recipeNumber: existing.recipeNumber || "",
      });
    } else {
      setDefaultsForm({
        companyName: form.companyName || "",
        source: form.source || "",
        recipeNumber: form.recipeNumber || "",
      });
    }
    setShowDefaultsEditor((prev) => !prev);
  };

  const handleDefaultsChange = (e) => {
    const { name, value } = e.target;
    setDefaultsForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveDefaults = () => {
    const currentName = form.itemName.trim();
    if (!currentName) {
      alert("Enter an item name to save defaults for.");
      return;
    }
    const existing = findItemByName(currentName);
    if (existing) {
      setItems((prev) =>
        prev.map((it) =>
          it.id === existing.id
            ? {
                ...it,
                companyName: defaultsForm.companyName.trim(),
                source: defaultsForm.source.trim(),
                recipeNumber: defaultsForm.recipeNumber.trim(),
              }
            : it
        )
      );
    } else {
      const newItem = {
        id: Date.now() + 2,
        name: currentName,
        companyName: defaultsForm.companyName.trim(),
        source: defaultsForm.source.trim(),
        recipeNumber: defaultsForm.recipeNumber.trim(),
      };
      setItems((prev) => [...prev, newItem]);
    }
    alert("Item defaults saved.");
  };

  const handleApplyDefaultsToBatch = () => {
    setForm((prev) => ({
      ...prev,
      companyName: defaultsForm.companyName || "",
      source: defaultsForm.source || "",
      recipeNumber: defaultsForm.recipeNumber || "",
    }));
  };

  // ✅ THERMAL ROLL PRINT (58×40) — BIGGER TEXT but still fits frame
  const printBatch = (batch) => {
    if (!batch) return;
    if (typeof window === "undefined") return;

    const escapeHtml = (str) => {
      if (!str) return "";
      return String(str)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    };

    const total = Number(batch.count || 0);
    if (!total || total <= 0) {
      alert("Batch has no labels to print.");
      return;
    }

    const W = 58;
    const H = 40;
    const M = Math.max(0, Math.min(4, Number(printSettings.rollMarginMm ?? 1.5)));
    const fontScale = Math.max(0.75, Math.min(1.25, Number(printSettings.rollFontScale || 1.12)));
    const rotate = !!printSettings.rotate;
    const dense = !!printSettings.dense;

    const pages = Array.from({ length: total }).map((_, idx) => {
      const pieceNo = idx + 1;
      return `
        <div class="label-page">
          <div class="label">
            <div class="company">${escapeHtml(batch.companyName || "")}</div>
            <div class="item">${escapeHtml(batch.itemName || "")}</div>

            <div class="line"><span class="k">SRC</span> ${escapeHtml(batch.source || "—")}</div>
            <div class="line"><span class="k">RCP</span> ${escapeHtml(batch.recipeNumber || "—")}</div>

            <div class="line">
              <span class="k">BID</span> ${escapeHtml(batch.id)}
              <span class="sep">•</span>
              <span class="k">#</span> ${pieceNo}/${total}
            </div>

            <div class="line">
              <span class="k">P</span> ${escapeHtml(batch.productionDate || "")}
              <span class="sep">•</span>
              <span class="k">E</span> ${escapeHtml(batch.expiryDate || "")}
            </div>
          </div>
        </div>
      `;
    });

    const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>XP-365B Roll Labels 58x40 - ${escapeHtml(batch.itemName)}</title>
  <style>
    @page { size: ${W}mm ${H}mm; margin: ${M}mm; }

    html, body {
      margin: 0; padding: 0;
      background: #fff; color: #000;
      font-family: Arial, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    * { box-sizing: border-box; }

    /* One label per page */
    .label-page {
      width: ${W}mm;
      height: ${H}mm;
      page-break-after: always;
      break-after: page;
      display: flex;
      align-items: stretch;
      justify-content: stretch;
    }

    .label {
      flex: 1;
      border: 0.25mm solid #000;

      /* tighter padding frees space for bigger fonts */
      padding: ${dense ? "1.1mm 1.3mm" : "1.4mm 1.6mm"};
      overflow: hidden;

      display: flex;
      flex-direction: column;

      /* tighter vertical gap */
      gap: ${dense ? "0.45mm" : "0.65mm"};

      transform-origin: top left;
      transform: ${rotate ? "rotate(90deg) scale(" + fontScale + ")" : "scale(" + fontScale + ")"};
    }

    /* bigger header but stays inside */
    .company {
      font-weight: 900;
      font-size: ${dense ? "10.4pt" : "10.8pt"};
      text-align: center;
      text-transform: uppercase;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    /* allow item up to 2 lines to keep readability */
    .item {
      font-weight: 900;
      font-size: ${dense ? "9.6pt" : "10.0pt"};
      text-align: center;

      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;

      overflow: hidden;
      margin-bottom: ${dense ? "0.1mm" : "0.2mm"};
      line-height: 1.05;
    }

    /* meta lines bigger but safe */
    .line {
      font-size: ${dense ? "7.9pt" : "8.2pt"};
      line-height: 1.05;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .k { font-weight: 900; }
    .sep { font-weight: 900; padding: 0 0.8mm; }
  </style>
</head>
<body>
  ${pages.join("")}
  <script>
    window.onload = () => {
      setTimeout(() => { window.focus(); window.print(); }, 120);
    };
  </script>
</body>
</html>
    `;

    const w = window.open("", "_blank");
    if (!w) {
      alert("Popup blocked. Please allow popups for printing.");
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  // ---------- RENDER ----------
  if (!showModal) return null;

  return (
    <div className="batch-manager-root">
      <div className="modal-overlay">
        <div className="modal-content compact">
          {/* Title + close */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: "0.95rem" }}>CK — Central Kitchen Batches</h3>
              <p style={{ margin: "2px 0 0", fontSize: 11, opacity: 0.8 }}>
                Default printing: <b>XPrinter XP-365B</b> — Roll labels <b>58×40mm</b> (bigger text, same frame).
              </p>
            </div>
            <button className="btn ghost small" onClick={closeModal}>Close</button>
          </div>

          {/* XP-365B Roll Print Settings */}
          <div className="print-settings-card" style={{ marginBottom: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
              <div style={{ fontWeight: 700, opacity: 0.95 }}>XP-365B Roll Label Settings (58×40)</div>
              <div style={{ fontSize: 10, opacity: 0.85 }}>
                In print dialog: choose <b>XPrinter XP-365B</b>, Paper <b>58×40mm</b>, Scale <b>100%</b>, disable “Fit”.
              </div>
            </div>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 8 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span style={{ fontSize: 10, opacity: 0.85 }}>Margin (mm)</span>
                <input
                  name="print_rollMarginMm"
                  type="number"
                  min="0"
                  max="4"
                  step="0.5"
                  value={printSettings.rollMarginMm}
                  onChange={handleChangeGeneric}
                  style={{ width: 120 }}
                />
                <span style={{ fontSize: 10, opacity: 0.7 }}>If clipping: set 2.0mm</span>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span style={{ fontSize: 10, opacity: 0.85 }}>Font scale</span>
                <input
                  name="print_rollFontScale"
                  type="number"
                  min="0.75"
                  max="1.25"
                  step="0.05"
                  value={printSettings.rollFontScale}
                  onChange={handleChangeGeneric}
                  style={{ width: 120 }}
                />
                <span style={{ fontSize: 10, opacity: 0.7 }}>If overflow: reduce to 1.05</span>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, opacity: 0.9 }}>
                  <input type="checkbox" name="print_dense" checked={!!printSettings.dense} onChange={handleChangeGeneric} />
                  Dense layout (recommended)
                </label>

                <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, opacity: 0.9 }}>
                  <input type="checkbox" name="print_rotate" checked={!!printSettings.rotate} onChange={handleChangeGeneric} />
                  Rotate 90° (only if sideways)
                </label>
              </div>
            </div>

            <div style={{ marginTop: 8, fontSize: 10, opacity: 0.8, borderTop: "1px solid rgba(148,163,184,0.25)", paddingTop: 6 }}>
              If Windows asks for an “output file” only on XP-365B: fix printer port/driver (Ports must be USB00x / TCP-IP, not FILE:).
            </div>
          </div>

          {/* Stats + export + history toggle */}
          <div className="batch-manager-header">
            <div className="batch-manager-header-main">
              <button type="button" className="btn ghost small" onClick={resetForm}>New batch</button>

              {batches.length > 0 && (
                <div className="batch-manager-stats">
                  <span>{batches.length} batch(es), {totalLabels} label(s) total.</span>
                  {lastBatch && (
                    <button type="button" className="btn ghost small" onClick={() => printBatch(lastBatch)}>
                      Print last: {lastBatch.itemName} ({lastBatch.count})
                    </button>
                  )}
                </div>
              )}
            </div>

            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {batches.length > 0 && (
                <>
                  <button type="button" className="btn ghost small" onClick={() => exportCsv("all")}>Export all CSV</button>
                  <button type="button" className="btn ghost small" onClick={() => exportCsv("filtered")} disabled={!filteredBatches.length}>
                    Export filtered
                  </button>
                </>
              )}

              <button type="button" className="btn ghost small" onClick={() => setShowHistory((prev) => !prev)}>
                {showHistory ? "Hide history" : "Batch history"}
              </button>
            </div>
          </div>

          {/* Optional sync error */}
          {syncError && (
            <div
              style={{
                marginBottom: 6,
                fontSize: 10,
                color: "#fecaca",
                padding: "4px 6px",
                borderRadius: 6,
                border: "1px solid rgba(248,113,113,0.6)",
                background: "rgba(127,29,29,0.4)",
              }}
            >
              {syncError}
            </div>
          )}

          {/* History */}
          {showHistory && (
            <div className="batch-history-panel">
              <div className="batch-history-header">
                <div className="batch-history-title">Batches history & search</div>
                <div style={{ fontSize: 10, opacity: 0.8 }}>
                  Showing {filteredBatches.length} / {batches.length} batch(es)
                </div>
              </div>

              <div className="batch-filters-row">
                <input
                  type="text"
                  name="query"
                  value={filters.query}
                  onChange={handleFilterChange}
                  placeholder="Search by item, company, source, recipe..."
                />
                <input type="date" name="from" value={filters.from} onChange={handleFilterChange} />
                <input type="date" name="to" value={filters.to} onChange={handleFilterChange} />
              </div>

              <div className="batch-history-list">
                {filteredBatches.length === 0 ? (
                  <div style={{ fontSize: 11, opacity: 0.8 }}>No batches matching current filter.</div>
                ) : (
                  filteredBatches.map((b) => (
                    <div key={b.id} className="batch-history-item">
                      <div className="batch-history-item-main">
                        <div className="batch-history-item-text">
                          <div><strong>{b.itemName}</strong> — {b.count} pcs</div>
                          <div className="batch-history-meta">
                            {b.companyName && <>Company: {b.companyName} | </>}
                            Source: {b.source || "—"} | Recipe: {b.recipeNumber || "—"}
                          </div>
                          <div className="batch-history-meta">
                            Prod: {b.productionDate} → Exp: {b.expiryDate}
                          </div>
                        </div>

                        <div className="batch-history-actions">
                          <button type="button" className="btn ghost small" onClick={() => printBatch(b)}>Print</button>
                          <button type="button" className="btn ghost small" onClick={() => useAsTemplate(b)}>Template</button>
                          <button
                            type="button"
                            className="btn ghost small"
                            onClick={() => syncBatchToBackend(b)}
                            disabled={syncingId === b.id}
                          >
                            {syncingId === b.id ? "Syncing..." : "Sync"}
                          </button>
                          <button
                            type="button"
                            className="btn ghost small"
                            style={{ color: "#fecaca" }}
                            onClick={() => deleteBatch(b.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* MAIN FORM + PREVIEW */}
          <form onSubmit={handleSubmit}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 1.3fr) minmax(0, 1fr)",
                gap: 10,
                alignItems: "flex-start",
              }}
            >
              {/* Left */}
              <div>
                <div className="customize-row">
                  <span className="customize-label">Item (combo box)</span>
                  <div className="customize-controls">
                    <div style={{ display: "flex", gap: 6, alignItems: "center", width: "100%" }}>
                      <input
                        type="text"
                        list="batch-item-datalist"
                        name="itemName"
                        value={form.itemName}
                        onChange={handleItemNameChange}
                        placeholder={items.length ? "Type or select saved item..." : "Type item name..."}
                        style={{ flex: 1 }}
                      />
                      <button type="button" className="btn ghost small" style={{ whiteSpace: "nowrap" }} onClick={toggleDefaultsEditor}>
                        Defaults
                      </button>
                    </div>

                    <datalist id="batch-item-datalist">
                      {items.map((it) => <option key={it.id} value={it.name} />)}
                    </datalist>

                    {errors.itemName && <div className="field-error">{errors.itemName}</div>}
                    {items.length > 0 && (
                      <div style={{ fontSize: 10, opacity: 0.7 }}>
                        Selecting an item auto-fills company & source from saved defaults.
                      </div>
                    )}
                  </div>
                </div>

                {showDefaultsEditor && (
                  <div className="item-defaults-card">
                    <div className="item-defaults-header">
                      <span>
                        Item defaults for: <strong>{form.itemName.trim() || "(no item name)"}</strong>
                      </span>
                      <button type="button" className="btn ghost small" onClick={() => setShowDefaultsEditor(false)}>Close</button>
                    </div>

                    <div style={{ fontSize: 10, opacity: 0.8, marginBottom: 4 }}>
                      Saved once per item and reused for future batches.
                    </div>

                    <div className="item-defaults-fields">
                      <div>
                        <div>Default company</div>
                        <input type="text" name="companyName" value={defaultsForm.companyName} onChange={handleDefaultsChange} placeholder="e.g. Bamboo" />
                      </div>
                      <div>
                        <div>Default source</div>
                        <input type="text" name="source" value={defaultsForm.source} onChange={handleDefaultsChange} placeholder="e.g. Central Kitchen" />
                      </div>
                      <div>
                        <div>Default recipe #</div>
                        <input type="text" name="recipeNumber" value={defaultsForm.recipeNumber} onChange={handleDefaultsChange} placeholder="e.g. R-102" />
                      </div>
                    </div>

                    <div className="item-defaults-actions">
                      <button type="button" className="btn primary small" onClick={handleSaveDefaults}>Save defaults</button>
                      <button type="button" className="btn ghost small" onClick={handleApplyDefaultsToBatch}>Apply to this batch</button>
                    </div>
                  </div>
                )}

                <div className="customize-row">
                  <span className="customize-label">Company name</span>
                  <div className="customize-controls">
                    <input type="text" name="companyName" value={form.companyName} onChange={handleChangeGeneric} placeholder="e.g. Bamboo" style={{ width: "100%" }} />
                    {errors.companyName && <div className="field-error">{errors.companyName}</div>}
                  </div>
                </div>

                <div className="customize-row">
                  <span className="customize-label">Source</span>
                  <div className="customize-controls">
                    <input type="text" name="source" value={form.source} onChange={handleChangeGeneric} placeholder="e.g. Central Kitchen, Supplier X..." style={{ width: "100%" }} />
                  </div>
                </div>

                <div className="customize-row">
                  <span className="customize-label">Recipe #</span>
                  <div className="customize-controls">
                    <input type="text" name="recipeNumber" value={form.recipeNumber} onChange={handleChangeGeneric} placeholder="e.g. R-102" style={{ width: "100%" }} />
                  </div>
                </div>

                <div className="customize-row">
                  <span className="customize-label">Production date</span>
                  <div className="customize-controls">
                    <input type="date" name="productionDate" value={form.productionDate} onChange={handleChangeGeneric} style={{ width: "100%" }} />
                    {errors.productionDate && <div className="field-error">{errors.productionDate}</div>}
                  </div>
                </div>

                <div className="customize-row">
                  <span className="customize-label">
                    Expiry date <span style={{ fontSize: 10, opacity: 0.8 }}>(or auto)</span>
                  </span>
                  <div className="customize-controls">
                    <div style={{ display: "flex", gap: 6, alignItems: "center", width: "100%", flexWrap: "wrap" }}>
                      <input type="date" name="expiryDate" value={form.expiryDate} onChange={handleChangeGeneric} style={{ flex: "0 0 140px" }} />
                      <input type="number" name="shelfLifeDays" min="1" step="1" value={form.shelfLifeDays} onChange={handleChangeGeneric} placeholder="Shelf life (days)" style={{ flex: "0 0 130px" }} />
                      <button type="button" className="btn ghost small" onClick={handleAutoExpiry}>Auto-fill</button>
                    </div>
                    {errors.expiryDate && <div className="field-error">{errors.expiryDate}</div>}
                    {errors.shelfLifeDays && <div className="field-error">{errors.shelfLifeDays}</div>}
                  </div>
                </div>

                <div className="customize-row">
                  <span className="customize-label">Count (labels)</span>
                  <div className="customize-controls">
                    <input type="number" name="count" min="1" step="1" value={form.count} onChange={handleChangeGeneric} placeholder="e.g. 50" style={{ width: "100%" }} />
                    {errors.count && <div className="field-error">{errors.count}</div>}
                  </div>
                </div>

                <div className="customize-row">
                  <span className="customize-label">Sync to backend</span>
                  <div className="customize-controls">
                    <label style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, cursor: "pointer", opacity: 0.9 }}>
                      <input type="checkbox" name="autoSync" checked={autoSync} onChange={handleChangeGeneric} />
                      <span>Auto send batch to Inventory / CK <code style={{ fontSize: 10 }}>(POST /inventory/batches)</code></span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Right */}
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <div className="label-preview-card">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                    <span style={{ fontSize: 11, opacity: 0.9, fontWeight: 500 }}>Label preview (58×40)</span>
                    {form.count && <span style={{ fontSize: 10, opacity: 0.7 }}>{form.count} label(s)</span>}
                  </div>

                  {hasPreviewData ? (
                    <div className="label-preview-inner">
                      <div style={{ fontWeight: 900, fontSize: 12, textAlign: "center", textTransform: "uppercase" }}>
                        {form.companyName || "Company name"}
                      </div>
                      <div style={{ fontWeight: 900, fontSize: 11, textAlign: "center", marginTop: 2 }}>
                        {form.itemName || "Item name"}
                      </div>
                      <div style={{ marginTop: 4, fontSize: 11 }}>
                        <div><strong>SRC:</strong> {form.source || "—"}</div>
                        <div><strong>RCP:</strong> {form.recipeNumber || "—"}</div>
                        <div><strong>P:</strong> {form.productionDate || "--/--/----"}</div>
                        <div><strong>E:</strong> {form.expiryDate || "--/--/----"}</div>
                      </div>
                    </div>
                  ) : (
                    <div style={{ fontSize: 11, opacity: 0.7, fontStyle: "italic" }}>
                      Start typing to see a preview of the label…
                    </div>
                  )}
                </div>

                {batches.length > 0 && (
                  <div className="customize-row" style={{ marginTop: 4 }}>
                    <span className="customize-label">Recent batches</span>
                    <div
                      className="customize-controls"
                      style={{
                        flexDirection: "column",
                        alignItems: "flex-start",
                        gap: 4,
                        maxHeight: 180,
                        overflowY: "auto",
                        fontSize: 11,
                        width: "100%",
                      }}
                    >
                      {batches.slice(0, 6).map((b) => (
                        <div
                          key={b.id}
                          style={{
                            opacity: 0.95,
                            borderBottom: "1px solid rgba(148,163,184,0.35)",
                            paddingBottom: 4,
                            width: "100%",
                          }}
                        >
                          <div style={{ display: "flex", justifyContent: "space-between", gap: 6, alignItems: "flex-start" }}>
                            <div style={{ minWidth: 0 }}>
                              <b>{b.itemName}</b> — {b.count} pcs
                              <div>{b.companyName ? <>Company: {b.companyName} | </> : null}Source: {b.source || "—"}</div>
                              <div>Recipe: {b.recipeNumber || "—"} | Prod: {b.productionDate} → Exp: {b.expiryDate}</div>
                            </div>

                            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                              <button type="button" className="btn ghost small" style={{ fontSize: 10, padding: "2px 6px", whiteSpace: "nowrap" }} onClick={() => printBatch(b)}>
                                Print
                              </button>
                              <button type="button" className="btn ghost small" style={{ fontSize: 10, padding: "2px 6px", whiteSpace: "nowrap" }} onClick={() => useAsTemplate(b)}>
                                Template
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 10 }}>
              <button type="button" className="btn ghost small" onClick={closeModal}>Cancel</button>
              <button type="submit" className="btn primary small">Save batch</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
